
import { Bot, Briefcase, Building, Calendar, CloudLightning, FileText, Gem, Globe, Linkedin, Mail, MessageSquare, Phone, Star, Users, Wrench, Zap } from "lucide-react";

export interface PlatformConfig {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  category: 'ai' | 'recruiting' | 'communication' | 'calendar' | 'tools';
  documentationUrl?: string;
  fields: Array<{
    name: string;
    label: string;
    type: 'text' | 'password' | 'email' | 'url';
    placeholder?: string;
    required?: boolean;
    description?: string;
  }>;
}

export const platformConfigurations: PlatformConfig[] = [
  // AI Services
  {
    id: 'openai',
    name: 'OpenAI',
    description: 'GPT models for AI-powered conversations and analysis',
    icon: Bot,
    category: 'ai',
    documentationUrl: 'https://platform.openai.com/api-keys',
    fields: [
      { 
        name: 'api_key', 
        label: 'API Key', 
        type: 'password', 
        placeholder: 'sk-...', 
        required: true,
        description: 'Found in your OpenAI dashboard under API keys'
      }
    ]
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    description: 'Claude AI models for advanced reasoning and analysis',
    icon: CloudLightning,
    category: 'ai',
    documentationUrl: 'https://console.anthropic.com/account/keys',
    fields: [
      { 
        name: 'api_key', 
        label: 'API Key', 
        type: 'password', 
        placeholder: 'sk-ant-...', 
        required: true,
        description: 'Found in your Anthropic Console'
      }
    ]
  },
  {
    id: 'gemini',
    name: 'Google Gemini',
    description: 'Google\'s advanced AI models for multimodal tasks',
    icon: Gem,
    category: 'ai',
    documentationUrl: 'https://makersuite.google.com/app/apikey',
    fields: [
      { 
        name: 'api_key', 
        label: 'API Key', 
        type: 'password', 
        placeholder: 'AIza...', 
        required: true,
        description: 'Found in your Google AI Studio'
      }
    ]
  },
  {
    id: 'elevenlabs',
    name: 'ElevenLabs',
    description: 'AI voice generation and speech synthesis',
    icon: MessageSquare,
    category: 'ai',
    documentationUrl: 'https://docs.elevenlabs.io/api-reference/authentication',
    fields: [
      { 
        name: 'api_key', 
        label: 'API Key', 
        type: 'password', 
        placeholder: 'sk_...', 
        required: true,
        description: 'Found in your ElevenLabs account settings'
      }
    ]
  },

  // Recruiting Platforms
  {
    id: 'linkedin',
    name: 'LinkedIn',
    description: 'Professional networking and candidate sourcing',
    icon: Linkedin,
    category: 'recruiting',
    documentationUrl: 'https://learn.microsoft.com/en-us/linkedin/shared/authentication/authentication',
    fields: [
      { 
        name: 'api_key', 
        label: 'API Key', 
        type: 'password', 
        placeholder: 'Enter LinkedIn API key', 
        required: true,
        description: 'Found in your LinkedIn Developer Console'
      },
      { 
        name: 'client_id', 
        label: 'Client ID', 
        type: 'text', 
        placeholder: 'Enter LinkedIn Client ID', 
        required: true 
      }
    ]
  },
  {
    id: 'indeed',
    name: 'Indeed',
    description: 'Job board integration for candidate sourcing',
    icon: Briefcase,
    category: 'recruiting',
    documentationUrl: 'https://developer.indeed.com/docs/indeed-jobs-api/#getting-started',
    fields: [
      { 
        name: 'api_key', 
        label: 'API Key', 
        type: 'password', 
        placeholder: 'Enter Indeed API key', 
        required: true,
        description: 'Available in your Indeed Publisher Account'
      }
    ]
  },

  // Tools & Utilities
  {
    id: 'resend',
    name: 'Resend',
    description: 'Email delivery service for transactional emails',
    icon: Mail,
    category: 'tools',
    fields: [
      { 
        name: 'api_key', 
        label: 'API Key', 
        type: 'password', 
        placeholder: 're_...', 
        required: true 
      }
    ]
  },
  {
    id: 'scrapeless',
    name: 'Scrapeless',
    description: 'Web scraping and data enrichment service',
    icon: Wrench,
    category: 'tools',
    fields: [
      { 
        name: 'api_key', 
        label: 'API Key', 
        type: 'password', 
        placeholder: 'Enter Scrapeless API key', 
        required: true 
      }
    ]
  },
  {
    id: 'lutra-webhook',
    name: 'Lutra Job Search',
    description: 'Job search automation and candidate sourcing',
    icon: Bot,
    category: 'tools',
    fields: [
      { 
        name: 'webhookUrl', 
        label: 'Webhook URL', 
        type: 'url', 
        placeholder: 'https://lutra.ai/api/webhooks/...', 
        required: true,
        description: 'Your Lutra webhook URL for job search automation'
      },
      { 
        name: 'webhookToken', 
        label: 'Webhook Token', 
        type: 'password', 
        placeholder: 'Enter your webhook token', 
        required: true,
        description: 'Authentication token for your Lutra webhook'
      }
    ]
  }
];

// Helper function to get platforms by category
export const getPlatformsByCategory = (category: string) => {
  return platformConfigurations.filter(platform => platform.category === category);
};

// Helper function to get all categories
export const getCategories = () => {
  const categories = [...new Set(platformConfigurations.map(platform => platform.category))];
  return categories.sort();
};
